<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('holidays.title')" :description="t('holidays.description')">
      <template #actions>
        <AppButton variant="outline" @click="exportHolidays">
          <Download class="h-4 w-4 mr-2" />{{ t('holidays.export') }}
        </AppButton>
        <AppButton variant="outline" @click="importHolidays">
          <Upload class="h-4 w-4 mr-2" />{{ t('holidays.import') }}
        </AppButton>
        <AppButton @click="showAddHolidayModal = true">
          <Plus class="h-4 w-4 mr-2" />{{ t('holidays.addHoliday') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Year selector + stat cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Year control -->
      <AppCard>
        <div class="p-4">
          <p class="text-xs font-medium text-muted-foreground mb-2">{{ t('holidays.year') }}</p>
          <div class="flex items-center gap-2">
            <AppButton size="icon-sm" variant="outline" @click="changeYear(-1)">
              <ChevronLeft class="h-3.5 w-3.5" />
            </AppButton>
            <select v-model="selectedYear" @change="loadHolidays" class="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
            <AppButton size="icon-sm" variant="outline" @click="changeYear(1)">
              <ChevronRight class="h-3.5 w-3.5" />
            </AppButton>
          </div>
        </div>
      </AppCard>
      <AppStatCard :label="t('holidays.totalHolidays')" :value="holidays.length" :icon="Calendar" iconBg="bg-blue-50" iconColor="text-blue-600" :loading="loading" />
      <AppStatCard :label="t('holidays.upcoming')" :value="upcomingHolidays.length" :icon="Clock" iconBg="bg-emerald-50" iconColor="text-emerald-600" :loading="loading" />
      <AppStatCard :label="t('holidays.longWeekends')" :value="longWeekends" :icon="Sun" iconBg="bg-amber-50" iconColor="text-amber-600" :loading="loading" />
    </div>

    <!-- Category filter + holidays list -->
    <AppCard>
      <!-- Category filter -->
      <div class="px-4 py-3 border-b border-border flex flex-wrap gap-2 items-center">
        <button
          @click="selectedCategory = ''"
          :class="['px-3 py-1 rounded-full text-xs font-medium transition-colors', selectedCategory === '' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80']"
        >
          {{ t('holidays.allCategories') }} ({{ holidays.length }})
        </button>
        <button
          v-for="cat in holidayCategories"
          :key="cat.id"
          @click="selectedCategory = cat.id"
          :class="['px-3 py-1 rounded-full text-xs font-medium transition-colors', selectedCategory === cat.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80']"
        >
          {{ cat.name }} ({{ getHolidaysByCategory(cat.id).length }})
        </button>
      </div>

      <!-- Header -->
      <div class="px-4 py-3 border-b border-border">
        <p class="text-sm font-medium text-foreground">{{ t('holidays.holidaysFor') }} {{ selectedYear }}</p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12"><AppSpinner size="lg" /></div>
      <AppEmptyState v-else-if="filteredHolidays.length === 0" :icon="Calendar" :title="t('holidays.noHolidays')" :description="t('holidays.noHolidaysDesc')" />

      <div v-else class="divide-y divide-border">
        <div v-for="holiday in filteredHolidays" :key="holiday.id" class="p-4 hover:bg-muted/30 transition-colors">
          <div class="flex items-start justify-between gap-4">
            <!-- Date block -->
            <div class="h-14 w-12 rounded-lg bg-primary/10 flex flex-col items-center justify-center shrink-0">
              <span class="text-lg font-bold text-primary leading-tight">{{ formatDay(holiday.date) }}</span>
              <span class="text-[10px] text-primary/70 uppercase font-semibold">{{ formatMonth(holiday.date) }}</span>
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-1">
                <span class="text-sm font-semibold text-foreground">{{ holiday.name }}</span>
                <AppBadge :variant="holiday.category === 'national' ? 'default' : holiday.category === 'religious' ? 'secondary' : 'muted'">
                  {{ getCategoryName(holiday.category) }}
                </AppBadge>
                <span v-if="holiday.isRecurring" class="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600">
                  <Repeat class="h-3 w-3" />Recurring
                </span>
                <span v-if="holiday.isOptional" class="text-[10px] text-muted-foreground">(Optional)</span>
              </div>
              <p class="text-xs text-muted-foreground">{{ formatFullDate(holiday.date) }}</p>
              <p v-if="holiday.description" class="text-xs text-muted-foreground mt-1">{{ holiday.description }}</p>
            </div>

            <!-- Status + actions -->
            <div class="flex items-center gap-2 shrink-0">
              <AppBadge v-if="isUpcoming(holiday.date)" variant="success">{{ getDaysUntil(holiday.date) }}d away</AppBadge>
              <AppBadge v-else-if="isPast(holiday.date)" variant="muted">Past</AppBadge>
              <AppBadge v-else variant="default">Today</AppBadge>
              <AppButton size="icon-sm" variant="ghost" @click="editHoliday(holiday)"><Edit class="h-3.5 w-3.5" /></AppButton>
              <AppButton size="icon-sm" variant="ghost" class="text-destructive" @click="deleteHoliday(holiday.id)"><Trash2 class="h-3.5 w-3.5" /></AppButton>
            </div>
          </div>
        </div>
      </div>
    </AppCard>

    <!-- Add/Edit Modal -->
    <AppModal v-model="showHolidayModal" :title="showEditHolidayModal ? t('holidays.editHolidayTitle') : t('holidays.addHolidayTitle')" size="md">
      <form @submit.prevent="saveHoliday" class="space-y-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('holidays.holidayName') }} <span class="text-destructive">*</span></label>
          <input type="text" v-model="holidayForm.name" required placeholder="e.g., Eid al-Fitr" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-foreground">{{ t('holidays.holidayDate') }} <span class="text-destructive">*</span></label>
            <input type="date" v-model="holidayForm.date" required class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-foreground">{{ t('holidays.category') }} <span class="text-destructive">*</span></label>
            <select v-model="holidayForm.category" required class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="">{{ t('holidays.allCategories') }}</option>
              <option v-for="cat in holidayCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('holidays.descriptionLabel') }}</label>
          <textarea v-model="holidayForm.description" rows="2" :placeholder="t('holidays.optional')" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
        <div class="flex gap-6 text-sm">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="holidayForm.isRecurring" class="rounded" />
            <span class="text-foreground">{{ t('holidays.isRecurring') }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" v-model="holidayForm.isOptional" class="rounded" />
            <span class="text-foreground">{{ t('holidays.affectsPayroll') }}</span>
          </label>
        </div>
      </form>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="closeModal">{{ t('holidays.cancel') }}</AppButton>
          <AppButton :disabled="submitting" @click="saveHoliday">
            {{ submitting ? t('holidays.save') : (showEditHolidayModal ? t('holidays.editHolidayTitle') : t('holidays.addHolidayTitle')) }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Import Modal -->
    <AppModal v-model="showImportModal" title="Import Holidays" size="sm">
      <div class="space-y-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Country</label>
          <select v-model="importCountry" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">Select country</option>
            <option value="US">United States</option>
            <option value="PK">Pakistan</option>
            <option value="IN">India</option>
            <option value="GB">United Kingdom</option>
            <option value="CA">Canada</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Year</label>
          <select v-model="importYear" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
        <div class="rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2.5 text-sm text-blue-700">
          <Info class="h-4 w-4 shrink-0 mt-0.5" />
          <p>This will import public holidays for the selected country and year. Existing holidays won't be duplicated.</p>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showImportModal = false">Cancel</AppButton>
          <AppButton variant="success" :disabled="!importCountry || !importYear || importing" @click="performImport">
            {{ importing ? 'Importing…' : 'Import Holidays' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Plus, Upload, Download, Calendar, Clock, Sun,
  ChevronLeft, ChevronRight, Edit, Trash2, X, Repeat, Info
} from 'lucide-vue-next';
import { AppPageHeader, AppStatCard, AppCard, AppBadge, AppButton, AppModal, AppSpinner, AppEmptyState } from '@/components/ui';
import dayjs from 'dayjs';
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';

const { t } = useI18n();

const loading = ref(false);
const submitting = ref(false);
const importing = ref(false);
const selectedYear = ref(dayjs().year());
const selectedCategory = ref('');
const holidays = ref([]);
const showAddHolidayModal = ref(false);
const showEditHolidayModal = ref(false);
const showImportModal = ref(false);
const editingHoliday = ref(null);
const importCountry = ref('');
const importYear = ref(dayjs().year());

const showHolidayModal = computed({
  get: () => showAddHolidayModal.value || showEditHolidayModal.value,
  set: (v) => { if (!v) closeModal(); }
});

const holidayForm = ref({
  name: '',
  date: '',
  category: '',
  description: '',
  isRecurring: false,
  isOptional: false
});

const holidayCategories = ref([
  { id: 'national', name: 'National Holidays', color: 'blue' },
  { id: 'religious', name: 'Religious Holidays', color: 'purple' },
  { id: 'cultural', name: 'Cultural Events', color: 'green' },
  { id: 'company', name: 'Company Events', color: 'orange' },
  { id: 'international', name: 'International Days', color: 'pink' }
]);

const availableYears = computed(() => {
  const currentYear = dayjs().year();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
});

const filteredHolidays = computed(() => {
  if (!selectedCategory.value) return holidays.value;
  return holidays.value.filter(holiday => holiday.category === selectedCategory.value);
});

const upcomingHolidays = computed(() => {
  const today = dayjs();
  return holidays.value.filter(holiday => dayjs(holiday.date).isAfter(today));
});

const longWeekends = computed(() => {
  // Calculate long weekends (holidays adjacent to weekends)
  return holidays.value.filter(holiday => {
    const date = dayjs(holiday.date);
    const dayOfWeek = date.day();
    return dayOfWeek === 1 || dayOfWeek === 5; // Monday or Friday
  }).length;
});

// Methods
const loadHolidays = async () => {
  try {
    loading.value = true;
    console.log('📅 Loading holidays for year:', selectedYear.value);
    
    const response = await attendanceService.holidays.admin.getHolidays({
      year: selectedYear.value
    });

    holidays.value = response.data?.holidays || [];
    console.log('✅ Holidays loaded:', holidays.value.length);
  } catch (error) {
    console.error('❌ Error loading holidays:', error);
    console.log('🔄 Loading mock data for development');
    loadMockHolidays();
  } finally {
    loading.value = false;
  }
};

const loadMockHolidays = () => {
  holidays.value = [
    {
      id: 1,
      name: 'New Year\'s Day',
      date: `${selectedYear.value}-01-01`,
      category: 'national',
      description: 'First day of the year',
      isRecurring: true,
      isOptional: false
    },
    {
      id: 2,
      name: 'Independence Day',
      date: `${selectedYear.value}-08-14`,
      category: 'national',
      description: 'Pakistan Independence Day',
      isRecurring: true,
      isOptional: false
    },
    {
      id: 3,
      name: 'Eid al-Fitr',
      date: `${selectedYear.value}-04-21`,
      category: 'religious',
      description: 'Festival of Breaking the Fast',
      isRecurring: true,
      isOptional: false
    },
    {
      id: 4,
      name: 'Christmas Day',
      date: `${selectedYear.value}-12-25`,
      category: 'religious',
      description: 'Celebration of the birth of Jesus Christ',
      isRecurring: true,
      isOptional: false
    }
  ];
};

const saveHoliday = async () => {
  try {
    submitting.value = true;

    const payload = {
      ...holidayForm.value,
      year: selectedYear.value
    };

    if (showEditHolidayModal.value) {
      console.log('✏️ Updating holiday:', editingHoliday.value.id);
      await attendanceService.holidays.admin.updateHoliday(editingHoliday.value.id, payload);
    } else {
      console.log('➕ Creating new holiday');
      await attendanceService.holidays.admin.createHoliday(payload);
    }

    closeModal();
    await loadHolidays();
    showToast('success', `Holiday ${showEditHolidayModal.value ? 'updated' : 'added'} successfully!`);
  } catch (error) {
    console.error('❌ Error saving holiday:', error);
    showToast('error', 'Failed to save holiday. Please try again.');
  } finally {
    submitting.value = false;
  }
};

const editHoliday = (holiday) => {
  editingHoliday.value = holiday;
  holidayForm.value = { ...holiday };
  showEditHolidayModal.value = true;
};

const deleteHoliday = async (holidayId) => {
  if (!confirm('Are you sure you want to delete this holiday?')) return;

  try {
    console.log('🗑️ Deleting holiday:', holidayId);
    await attendanceService.holidays.admin.deleteHoliday(holidayId);

    await loadHolidays();
    showToast('success', 'Holiday deleted successfully!');
  } catch (error) {
    console.error('❌ Error deleting holiday:', error);
    showToast('error', 'Failed to delete holiday. Please try again.');
  }
};

const closeModal = () => {
  showAddHolidayModal.value = false;
  showEditHolidayModal.value = false;
  editingHoliday.value = null;
  holidayForm.value = {
    name: '',
    date: '',
    category: '',
    description: '',
    isRecurring: false,
    isOptional: false
  };
};

const changeYear = (direction) => {
  selectedYear.value += direction;
  loadHolidays();
};

const importHolidays = () => {
  showImportModal.value = true;
};

const performImport = async () => {
  try {
    importing.value = true;
    console.log('📥 Importing holidays for:', importCountry.value, importYear.value);

    const response = await attendanceService.holidays.admin.importHolidays({
      country: importCountry.value,
      year: importYear.value
    });

    showImportModal.value = false;
    importCountry.value = '';
    
    if (importYear.value === selectedYear.value) {
      await loadHolidays();
    }
    
    showToast('success', `Successfully imported ${response.data?.imported || 0} holidays!`);
    console.log('✅ Holidays imported successfully');
  } catch (error) {
    console.error('❌ Error importing holidays:', error);
    showToast('error', 'Failed to import holidays. Please try again.');
  } finally {
    importing.value = false;
  }
};

const exportHolidays = async () => {
  try {
    console.log('📤 Exporting holidays for year:', selectedYear.value);
    
    const response = await attendanceService.holidays.admin.exportHolidays({
      year: selectedYear.value,
      format: 'ics'
    });

    const blob = new Blob([response.data], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `holidays-${selectedYear.value}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Holidays exported successfully');
    showToast('success', 'Holidays exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting holidays:', error);
    showToast('error', 'Failed to export holidays. Please try again.');
  }
};

const getHolidaysByCategory = (categoryId) => {
  return holidays.value.filter(holiday => holiday.category === categoryId);
};

const getCategoryName = (categoryId) => {
  const category = holidayCategories.value.find(cat => cat.id === categoryId);
  return category ? category.name : categoryId;
};

const getCategoryBadgeClass = (categoryId) => {
  const colorMap = {
    national: 'bg-blue-100 text-blue-800',
    religious: 'bg-purple-100 text-purple-800',
    cultural: 'bg-green-100 text-green-800',
    company: 'bg-orange-100 text-orange-800',
    international: 'bg-pink-100 text-pink-800'
  };
  return colorMap[categoryId] || 'bg-gray-100 text-gray-800';
};

const formatDay = (date) => {
  return dayjs(date).format('DD');
};

const formatMonth = (date) => {
  return dayjs(date).format('MMM');
};

const formatFullDate = (date) => {
  return dayjs(date).format('dddd, MMMM D, YYYY');
};

const isUpcoming = (date) => {
  return dayjs(date).isAfter(dayjs(), 'day');
};

const isPast = (date) => {
  return dayjs(date).isBefore(dayjs(), 'day');
};

const getDaysUntil = (date) => {
  return dayjs(date).diff(dayjs(), 'day');
};

// Toast notification helper
const showToast = (type, message) => {
  if (type === 'success') {
    console.log('✅', message);
  } else {
    console.error('❌', message);
  }
  // Simple alert for now - could be enhanced with proper toast component
  alert(message);
};

// Lifecycle
onMounted(() => {
  console.log('🔄 HolidayManagement mounted, loading holidays...');
  loadHolidays();
});
</script>

<style scoped>
.holiday-management {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
